import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import type { ApiSuccessResponse } from '@/types/api.types';
import { API } from '@/api/endpoints';
import { ENV } from '@/config/env';
import { useAuthStore } from '@/store/auth.store';
import { useNetworkStore } from '@/store/network.store';
import { isNetworkError } from '@/utils/errors';
import { refreshResponseSchema } from '@/features/auth/types/auth.types';

export const apiClient = axios.create({
  baseURL: ENV.API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  paramsSerializer: { indexes: null },
});

apiClient.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

function isBinaryResponseType(type: string | undefined): boolean {
  return type === 'blob' || type === 'arraybuffer';
}

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}[] = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) return resolve(token);
    reject(error);
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => {
    useNetworkStore.getState().setServerReachable(true);
    // File downloads come back raw, without the JSON envelope.
    if (isBinaryResponseType(response.config.responseType)) return response;
    const body = response.data as ApiSuccessResponse<unknown>;
    const hasMeta = body.meta !== undefined && body.meta !== null;
    const data = hasMeta ? { data: body.data, meta: body.meta } : body.data;
    return { ...response, data };
  },
  async (error: AxiosError<{ message?: string }>) => {
    const originalRequest = error.config as RetriableRequest | undefined;

    // A failed download still answers with the JSON error envelope, as a Blob.
    const errorData: unknown = error.response?.data;
    if (error.response && errorData instanceof Blob && errorData.type.includes('json')) {
      try {
        error.response.data = JSON.parse(await errorData.text()) as { message?: string };
      } catch {
        // Not JSON after all: keep axios' own message.
      }
    }

    const serverMessage = error.response?.data?.message;
    if (serverMessage) {
      error.message = serverMessage;
    }

    if (isNetworkError(error) && navigator.onLine) {
      useNetworkStore.getState().setServerReachable(false);
    } else if (error.response) {
      useNetworkStore.getState().setServerReachable(true);
    }

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url === API.auth.login ||
      originalRequest.url === API.auth.ssoLogin ||
      originalRequest.url === API.auth.refresh
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.post<{ data: unknown }>(
        `${ENV.API_BASE_URL}${API.auth.refresh}`,
        {},
        { withCredentials: true },
      );

      const parsed = refreshResponseSchema.parse(res.data.data);
      useAuthStore.getState().setAccessToken(parsed.accessToken);
      processQueue(null, parsed.accessToken);

      originalRequest.headers.Authorization = `Bearer ${parsed.accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        useAuthStore.getState().clearAuth();
      } else if (isNetworkError(refreshError) && navigator.onLine) {
        useNetworkStore.getState().setServerReachable(false);
      }
      return Promise.reject(
        refreshError instanceof Error ? refreshError : new Error(String(refreshError)),
      );
    } finally {
      isRefreshing = false;
    }
  },
);
